import ThrillFillSpillGuide from './ThrillFillSpillGuide'

interface Filters {
  roles: string[]
  sunlight: string[]
  water: string[]
  colors: string[]
  pestResistance: string[]
  onlyNative: boolean
  onlySafePets: boolean
  onlySafeChildren: boolean
  onlyPerennial: boolean
  windTolerance: string[]
  searchText: string
}

interface FilterPanelProps {
  filters: Filters
  onChange: (f: Filters) => void
  totalShown: number
  totalAll: number
}

function toggle(arr: string[], val: string): string[] {
  return arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val]
}

function Chip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button className={`filter-chip ${active ? 'active' : ''}`} onClick={onClick}>
      {label}
    </button>
  )
}

export default function FilterPanel({ filters, onChange, totalShown, totalAll }: FilterPanelProps) {
  const set = (patch: Partial<Filters>) => onChange({ ...filters, ...patch })

  return (
    <aside className="ac-card space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-pixel text-ac-brown text-sm">Filter Plants</h2>
        <span className="font-body text-xs text-ac-brown-light">{totalShown} / {totalAll}</span>
      </div>

      <input
        className="ac-input text-sm"
        placeholder="🔍 Search plants…"
        value={filters.searchText}
        onChange={e => set({ searchText: e.target.value })}
      />

      <div>
        <p className="font-body font-bold text-ac-brown text-xs mb-2 flex items-center gap-2">
          Role <ThrillFillSpillGuide />
        </p>
        <div className="flex flex-wrap gap-1.5">
          <Chip label="🌟 Thriller" active={filters.roles.includes('thriller')} onClick={() => set({ roles: toggle(filters.roles, 'thriller') })} />
          <Chip label="🌸 Filler"   active={filters.roles.includes('filler')}   onClick={() => set({ roles: toggle(filters.roles, 'filler') })} />
          <Chip label="✨ Spiller"  active={filters.roles.includes('spiller')}  onClick={() => set({ roles: toggle(filters.roles, 'spiller') })} />
        </div>
      </div>

      <div>
        <p className="font-body font-bold text-ac-brown text-xs mb-2">☀️ Sunlight</p>
        <div className="flex flex-wrap gap-1.5">
          <Chip label="Full Sun"     active={filters.sunlight.includes('full-sun')}      onClick={() => set({ sunlight: toggle(filters.sunlight, 'full-sun') })} />
          <Chip label="Part Shade"   active={filters.sunlight.includes('partial-shade')} onClick={() => set({ sunlight: toggle(filters.sunlight, 'partial-shade') })} />
          <Chip label="Full Shade"   active={filters.sunlight.includes('full-shade')}    onClick={() => set({ sunlight: toggle(filters.sunlight, 'full-shade') })} />
        </div>
      </div>

      <div>
        <p className="font-body font-bold text-ac-brown text-xs mb-2">💧 Water Needs</p>
        <div className="flex flex-wrap gap-1.5">
          <Chip label="Low"      active={filters.water.includes('low')}      onClick={() => set({ water: toggle(filters.water, 'low') })} />
          <Chip label="Moderate" active={filters.water.includes('moderate')} onClick={() => set({ water: toggle(filters.water, 'moderate') })} />
          <Chip label="High"     active={filters.water.includes('high')}     onClick={() => set({ water: toggle(filters.water, 'high') })} />
        </div>
      </div>

      <div>
        <p className="font-body font-bold text-ac-brown text-xs mb-2">🌈 Color</p>
        <div className="flex flex-wrap gap-1.5">
          {['pink','purple','blue','red','orange','yellow','white','silver','lime','green'].map(c => (
            <button
              key={c}
              onClick={() => set({ colors: toggle(filters.colors, c) })}
              className={`w-7 h-7 rounded-full border-2 transition-all ${filters.colors.includes(c) ? 'border-ac-brown scale-110 shadow-ac-sm' : 'border-ac-brown'}`}
              style={{ backgroundColor: colorHex(c) }}
              title={c}
            />
          ))}
        </div>
      </div>

      <div>
        <p className="font-body font-bold text-ac-brown text-xs mb-2">🐛 Pest Resistance</p>
        <div className="flex flex-wrap gap-1.5">
          <Chip label="High"     active={filters.pestResistance.includes('high')}     onClick={() => set({ pestResistance: toggle(filters.pestResistance, 'high') })} />
          <Chip label="Moderate" active={filters.pestResistance.includes('moderate')} onClick={() => set({ pestResistance: toggle(filters.pestResistance, 'moderate') })} />
          <Chip label="Low"      active={filters.pestResistance.includes('low')}      onClick={() => set({ pestResistance: toggle(filters.pestResistance, 'low') })} />
        </div>
      </div>

      <div>
        <p className="font-body font-bold text-ac-brown text-xs mb-2">💨 Wind Tolerance</p>
        <div className="flex flex-wrap gap-1.5">
          <Chip label="High"     active={filters.windTolerance.includes('high')}     onClick={() => set({ windTolerance: toggle(filters.windTolerance, 'high') })} />
          <Chip label="Moderate" active={filters.windTolerance.includes('moderate')} onClick={() => set({ windTolerance: toggle(filters.windTolerance, 'moderate') })} />
          <Chip label="Low"      active={filters.windTolerance.includes('low')}      onClick={() => set({ windTolerance: toggle(filters.windTolerance, 'low') })} />
        </div>
      </div>

      <div className="space-y-2 border-t-2 border-dashed border-ac-bg pt-3">
        <p className="font-body font-bold text-ac-brown text-xs">Safety & Special</p>
        {[
          { key: 'onlyNative', label: '🌿 Native plants only' },
          { key: 'onlySafePets', label: '🐾 Safe for pets' },
          { key: 'onlySafeChildren', label: '👶 Safe for children' },
          { key: 'onlyPerennial', label: '♾️ Perennials only' },
        ].map(({ key, label }) => (
          <label key={key} className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="rounded border-ac-brown accent-ac-green"
              checked={filters[key as keyof Filters] as boolean}
              onChange={e => set({ [key]: e.target.checked } as Partial<Filters>)}
            />
            <span className="font-body text-sm text-ac-brown">{label}</span>
          </label>
        ))}
      </div>

      <button
        className="ac-btn-outline w-full text-sm"
        onClick={() => onChange({
          roles: [], sunlight: [], water: [], colors: [], pestResistance: [],
          onlyNative: false, onlySafePets: false, onlySafeChildren: false, onlyPerennial: false,
          windTolerance: [], searchText: '',
        })}
      >
        🔄 Clear all filters
      </button>
    </aside>
  )
}

function colorHex(name: string): string {
  const map: Record<string, string> = {
    pink: '#f4a0b0', purple: '#c3a8e8', blue: '#90d5f0', red: '#f06060',
    orange: '#f9a05a', yellow: '#f9c74f', white: '#f5f5f5', silver: '#c0c8d0',
    lime: '#b8e890', green: '#5cb85c',
  }
  return map[name] ?? '#ccc'
}
